// src/utils/useAxios.js
import axios from "axios";
import dayjs from "dayjs";
import { jwtDecode } from "jwt-decode";
import { useContext, useMemo, useRef } from "react";

import AuthContext from "../context/AuthContext";
import { baseURL } from "./config";

const Swal = require("sweetalert2");

// NOTE : baseURL ici pointe la RACINE de l'API (sans slug), car les routes
// de refresh (/token/refresh/) sont elles-mêmes globales. Les appels vers les
// routes scopées établissement doivent passer une URL complète construite
// avec tenantURL(slug, "...") depuis les services (cf. utils/config.js).

// ─── État partagé entre toutes les instances (hors du hook) ────────────────
// Un seul refresh à la fois : avec ROTATE_REFRESH_TOKENS + BLACKLIST, deux
// refresh simultanés avec le même refresh token feraient échouer le second.
let refreshPromise = null;

// Évite d'ouvrir plusieurs popups "Session expirée" en même temps
let sessionExpiredShown = false;

const getStoredTokens = () => {
  try {
    return JSON.parse(localStorage.getItem("authTokens"));
  } catch {
    return null;
  }
};

const useAxios = () => {
  const ctx = useContext(AuthContext);

  // On garde toujours la dernière version du contexte, sans recréer l'instance
  const ctxRef = useRef(ctx);
  ctxRef.current = ctx;

  const axiosInstance = useMemo(() => {
    const instance = axios.create({ baseURL });

    const handleSessionExpired = async () => {
      if (sessionExpiredShown) return;
      sessionExpiredShown = true;

      await Swal.fire({
        title: "Session expirée",
        text: "Veuillez vous reconnecter.",
        icon: "warning",
        confirmButtonText: "Se reconnecter",
        allowOutsideClick: false,
      });

      ctxRef.current.logoutUser(true);
      sessionExpiredShown = false;
    };

    // ─── REQUÊTES : ajout du token + refresh automatique si expiré ─────────
    instance.interceptors.request.use(async (req) => {
      const tokens = getStoredTokens();
      if (!tokens) return req;

      let decoded;
      try {
        decoded = jwtDecode(tokens.access);
      } catch {
        return req;
      }

      const expired = dayjs.unix(decoded.exp).diff(dayjs()) < 1;

      if (!expired) {
        req.headers.Authorization = `Bearer ${tokens.access}`;
        return req;
      }

      try {
        if (!refreshPromise) {
          refreshPromise = axios
            .post(`${baseURL}/token/refresh/`, { refresh: tokens.refresh })
            .finally(() => {
              refreshPromise = null;
            });
        }

        const response = await refreshPromise;

        localStorage.setItem("authTokens", JSON.stringify(response.data));
        ctxRef.current.setAuthTokens(response.data);
        ctxRef.current.setUser(jwtDecode(response.data.access));

        req.headers.Authorization = `Bearer ${response.data.access}`;
        return req;
      } catch (error) {
        await handleSessionExpired();
        return Promise.reject(error);
      }
    });

    // ─── RÉPONSES : maintenance (503) et session invalide (401) ────────────
    instance.interceptors.response.use(
      (response) => response,

      async (error) => {
        if (error.response?.status === 503) {
          await Swal.fire({
            title: "🚧 Maintenance",
            text:
              error.response?.data?.error ||
              "Le site est actuellement en maintenance.",
            icon: "info",
            confirmButtonText: "Ok",
            allowOutsideClick: false,
          });

          if (getStoredTokens()) {
            ctxRef.current.logoutUser(true);
          }

          return Promise.reject(error);
        }

        if (error.response?.status === 401) {
          await handleSessionExpired();
        }

        return Promise.reject(error);
      }
    );

    return instance;
  }, []);

  return axiosInstance;
};

export default useAxios;